/**
 * S003 – TrialSopBuilder
 * Thin trial authorization layer for Supervisor Athena access.
 *
 * Architecture:
 *  - Reads argus_trial_token directly from sessionStorage (NOT AuthContext).
 *  - All API calls carry an explicit Authorization: Bearer <trial_token> header.
 *  - Real Athena business logic (transcribe + structure) is used unchanged.
 *  - Trial identity remains "trial"; AuthContext is never touched.
 *  - Normal /dashboard/sop-builder semantics are completely unchanged.
 */
import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Mic, Square, Loader2, ChefHat, CheckCircle2,
  ArrowLeft, ArrowRight, Upload
} from "lucide-react";
import { ShieldLogo } from "../components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Audio Recorder (inline – AudioRecorder in SOPBuilder.js is not exported) ──
const AudioRecorder = ({ onRecordingComplete, isProcessing }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        onRecordingComplete(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000);
    } catch {
      toast.error("Microphone access denied. Please allow microphone permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="flex flex-col items-center gap-4">
      <motion.button
        data-testid="trial-sop-record-btn"
        onClick={isRecording ? stopRecording : startRecording}
        disabled={isProcessing}
        className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
          isRecording ? "bg-red-500 animate-pulse" : "bg-gold hover:bg-gold/80"
        } ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
        whileHover={{ scale: isProcessing ? 1 : 1.05 }}
        whileTap={{ scale: isProcessing ? 1 : 0.95 }}
      >
        {isProcessing ? (
          <Loader2 className="w-10 h-10 text-indigo-700 animate-spin" />
        ) : isRecording ? (
          <Square className="w-10 h-10 text-white" />
        ) : (
          <Mic className="w-10 h-10 text-indigo-700" />
        )}
      </motion.button>
      {isRecording && (
        <p className="text-red-400 font-mono text-xl">{fmt(recordingTime)}</p>
      )}
      {!isRecording && !isProcessing && (
        <p className="text-xs text-slate-400">Click to start recording your SOP</p>
      )}
      {isProcessing && (
        <p className="text-xs text-gold animate-pulse">Processing audio with Athena...</p>
      )}
    </div>
  );
};

// ─── Main page ──────────────────────────────────────────────────────────────────
export default function TrialSopBuilder() {
  const navigate = useNavigate();
  const trialToken = sessionStorage.getItem("argus_trial_token");

  // Guard: must have a valid trial token
  useEffect(() => {
    if (!trialToken) navigate("/trial/login");
  }, [trialToken, navigate]);

  const [step, setStep] = useState("record"); // record | review | saved
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [transcript, setTranscript] = useState(null); // structured SOP from Athena
  const [savedSop, setSavedSop] = useState(null);

  const authHeader = { Authorization: `Bearer ${trialToken}` };

  // ── Step 1: Transcribe audio → structured SOP ──────────────────────────────
  const handleRecordingComplete = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");
      formData.append("department", "boh");
      const res = await axios.post(`${API}/sops/transcribe`, formData, {
        headers: { ...authHeader, "Content-Type": "multipart/form-data" },
      });
      setTranscript(res.data);
      setStep("review");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Transcription failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append("audio", file, file.name);
      formData.append("department", "boh");
      const res = await axios.post(`${API}/sops/transcribe`, formData, {
        headers: { ...authHeader, "Content-Type": "multipart/form-data" },
      });
      setTranscript(res.data);
      setStep("review");
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Step 2: Save the SOP ────────────────────────────────────────────────────
  const handleSaveSop = async () => {
    if (!transcript) return;
    setIsSaving(true);
    try {
      const payload = {
        title: transcript.title || "Untitled SOP",
        department: transcript.department || "boh",
        prep_time: transcript.prep_time || 0,
        yield_amount: transcript.yield_amount || 1,
        ingredients: transcript.ingredients || [],
        steps: transcript.steps || [],
        notes: transcript.notes || "",
        allergens: transcript.allergens || [],
      };
      const res = await axios.post(`${API}/sops`, payload, { headers: authHeader });
      setSavedSop(res.data.sop);
      setStep("saved");
      toast.success("SOP saved! Athena mission complete.");
      // Belt-and-suspenders: also fire the explicit progress update
      await axios.post(
        `${API}/trial/update-progress?mission=athena_tested&completed=true`,
        {},
        { headers: authHeader }
      ).catch(() => {}); // Non-fatal; server already updated on create_sop
    } catch (err) {
      toast.error("Save failed. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!trialToken) return null;

  return (
    <div className="min-h-screen bg-[#040F49]">
      {/* Header */}
      <header className="border-b border-gold/10 bg-[#040F49]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <ShieldLogo size={36} />
            <div>
              <span className="font-inter font-bold text-gold text-lg">Supervisor Athena</span>
              <span className="text-xs text-slate-500 block">SOP Builder · Trial</span>
            </div>
          </Link>
          <Link
            to="/trial/dashboard"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
            data-testid="back-to-dashboard-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        {/* Trial banner */}
        <div className="mb-8 px-4 py-3 rounded-xl border border-gold/30 bg-gold/5 text-center">
          <p className="text-sm text-gold">
            <ChefHat className="w-4 h-4 inline mr-1" />
            This SOP will be <strong>saved to your real Argus workspace</strong> and survives trial-to-membership conversion.
          </p>
        </div>

        {/* ── Step: Record ─────────────────────────────────────────────────── */}
        {step === "record" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A1244] rounded-2xl border border-gold/20 p-10 text-center"
          >
            <ChefHat className="w-12 h-12 text-orange-400 mx-auto mb-4" />
            <h1 className="text-2xl font-inter font-black text-white mb-2">
              Record Your First SOP
            </h1>
            <p className="text-slate-400 mb-8 max-w-md mx-auto">
              Speak your recipe or procedure aloud. Athena will transcribe, structure, and cost it in seconds.
            </p>
            <AudioRecorder
              onRecordingComplete={handleRecordingComplete}
              isProcessing={isProcessing}
            />
            <div className="mt-8 flex items-center gap-4 justify-center">
              <span className="text-xs text-slate-600">or upload an audio file</span>
              <label
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 cursor-pointer transition-colors text-sm"
                data-testid="trial-sop-upload-label"
              >
                <Upload className="w-4 h-4" />
                Upload Audio
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={handleFileUpload}
                  data-testid="trial-sop-upload-input"
                />
              </label>
            </div>
          </motion.div>
        )}

        {/* ── Step: Review ─────────────────────────────────────────────────── */}
        {step === "review" && transcript && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-[#0A1244] rounded-2xl border border-gold/20 p-8">
              <h2 className="text-xl font-inter font-bold text-white mb-1">
                {transcript.title || "Untitled SOP"}
              </h2>
              <p className="text-xs text-slate-500 mb-6">
                Dept: {transcript.department || "boh"} · Prep: {transcript.prep_time || 0} min · Serves: {transcript.yield_amount || 1}
              </p>

              {transcript.steps?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-bold text-gold mb-3">Steps</h3>
                  <ol className="space-y-2">
                    {transcript.steps.map((s, i) => (
                      <li key={i} className="flex gap-3 text-sm text-slate-300">
                        <span className="w-6 h-6 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center text-xs font-bold shrink-0">
                          {i + 1}
                        </span>
                        {s}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {transcript.total_cost != null && (
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gold/5 rounded-lg p-3 text-center border border-gold/20">
                    <p className="text-xs text-slate-400">Total Cost</p>
                    <p className="text-lg font-bold text-gold">${transcript.total_cost?.toFixed(2)}</p>
                  </div>
                  <div className="bg-gold/5 rounded-lg p-3 text-center border border-gold/20">
                    <p className="text-xs text-slate-400">Cost/Serving</p>
                    <p className="text-lg font-bold text-gold">${transcript.cost_per_serving?.toFixed(2)}</p>
                  </div>
                  <div className="bg-gold/5 rounded-lg p-3 text-center border border-gold/20">
                    <p className="text-xs text-slate-400">Suggested Price</p>
                    <p className="text-lg font-bold text-gold">${transcript.target_menu_price?.toFixed(2)}</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <Button
                  variant="ghost"
                  onClick={() => { setStep("record"); setTranscript(null); }}
                  className="text-slate-400 hover:text-white"
                  data-testid="trial-sop-retake-btn"
                >
                  Re-record
                </Button>
                <Button
                  onClick={handleSaveSop}
                  disabled={isSaving}
                  className="bg-gold hover:bg-gold/90 text-indigo-700 font-bold"
                  data-testid="trial-sop-save-btn"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save SOP
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Step: Saved ──────────────────────────────────────────────────── */}
        {step === "saved" && savedSop && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0A1244] rounded-2xl border border-lime/30 p-10 text-center"
            data-testid="trial-sop-saved-confirmation"
          >
            <CheckCircle2 className="w-16 h-16 text-lime mx-auto mb-4" />
            <h2 className="text-2xl font-inter font-black text-white mb-2">SOP Saved!</h2>
            <p className="text-slate-400 mb-2">
              <strong className="text-white">{savedSop.title}</strong> has been added to your Argus workspace.
            </p>
            <p className="text-xs text-slate-500 mb-8">
              This SOP is permanently saved under your trial restaurant and will be available when you become a Founding Member.
            </p>
            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => { setStep("record"); setTranscript(null); setSavedSop(null); }}
                className="border-slate-600 text-slate-300"
                data-testid="trial-sop-create-another-btn"
              >
                Create Another SOP
              </Button>
              <Button
                onClick={() => navigate("/trial/dashboard")}
                className="bg-gold hover:bg-gold/90 text-indigo-700 font-bold"
                data-testid="trial-sop-back-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
